import React, { useEffect, useState, useRef } from 'react';
import { Form } from 'react-final-form';
import { useParams } from 'react-router-dom';

import { Button } from '../../components/atoms/Button';
import { Heading } from '../../components/atoms/Heading';
import { Image } from '../../components/atoms/Image';
import { Text } from '../../components/atoms/Text';
import { Field } from '../../components/molecules/Field';
import { InfoCard } from '../../components/molecules/InfoCard';
import { StepCard } from '../../components/molecules/StepCard';
import { Card } from '../../components/molecules/Card';
import { Carousel } from '../../components/organisms/Carousel';

import { MainView } from '../../templates/MainView';
import actions from '../../actions';
import Illustration from '../../components/molecules/Illustration';
import HelpBlockImage from '../../assets/images/conversation.png';
import { useSelector } from 'react-redux';
import { capitalizeI } from '../../utils';

function ActionPlan() {
    const articleRef = useRef(null);
    const { layout } = useSelector((state) => state.layout);
    let { pathway, barrier, bct } = useParams();

    const [articles, setArticles] = useState([]);
    const [apps, setApps] = useState([]);
    const [actionList, setActionList] = useState([]);
    const [tip, setTip] = useState('');
    const [success, setSuccess] = useState(false);
    const [email, setEmail] = useState('');
    const bcts = useSelector((state) => state.bcts);

    const selectedBarrier = bcts[pathway][barrier];
    const isPopup = layout === 'popup';

    let actionStr = [];

    actionStr.push('I want to');
    switch (pathway) {
        case 'alcohol':
            actionStr.push('drink less');
            break;
        case 'smoking':
            actionStr.push('quit smoking');
            break;
        case 'weight':
            actionStr.push('manage my weight');
            break;
        default:
            // activity
            actionStr.push('get active');
    }

    actionStr.push('but');
    if (selectedBarrier) actionStr.push(selectedBarrier.label && capitalizeI(selectedBarrier.label.toLowerCase()));

    useEffect(() => {
        const uid = `*${barrier}-${bct}*`;
        const url = `https://www.livewelldorset.co.uk/json/articles.json?uid=${uid}`;
        fetch(url)
            .then((response) => response.json())
            .then((data) => {
                const art = [];
                const app = [];
                data.forEach((item) => {
                    if (item.article_type && item.article_type.toLowerCase() === 'download') {
                        app.push(item);
                    } else {
                        art.push(item);
                    }
                });

                setArticles(art);
                setApps(app);
            });

        const key = `bct${bct}`;
        if (actions[key]) {
            const action = actions[key];

            // get unique actions
            const uniqueActions = action?.actions.filter((v, i, a) => a.findIndex((t) => t.title === v.title) === i);

            setActionList(uniqueActions);
            setTip(action.tip);
        }
    }, [barrier, bct]);

    const sendEmail = (values) => {
        fetch('https://publicapi.livewelldorset.co.uk/habithacker/send', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: values.email,
                pathway,
                barrier,
                bct,
            }),
        }).then((data) => {
            setSuccess(true);
            setEmail(values.email);
        });
    };

    let showPurpleBanner = false;
    if (!articles.length && !apps.length) {
        showPurpleBanner = true; // articles pff, apps off, footer purple
    } else if (articles.length && !apps.length) {
        showPurpleBanner = false; // articles purple, apps off, footer white
    } else if (!articles.length && apps.length) {
        showPurpleBanner = false; // articles pff, apps purple, footer white
    } else if (articles.length && apps.length) {
        showPurpleBanner = true; // articles purple, apps white, footer purple
    }

    return (
        <MainView type="fullwidth">
            <section className="bg-primary-100">
                <div className="h-auto">
                    <Illustration type={`action-plan-${pathway}`} classes="md:min-h-[106px]" />
                </div>
                <div
                    className={`px-6 pt-6 pb-8 flex flex-col gap-6 w-full mx-auto md:py-12 md:w-3/4  ${
                        isPopup ? 'md:px-0' : 'md:px-12 md:w-3/4 lg:w-1/2'
                    }`}>
                    <Heading h1>Habit Hacker complete! Great job - you've taken your first steps toward change</Heading>
                    <Text body>
                        Thank you for answering my questions - identifying what’s blocking you and focusing on the
                        change you want to make is an important first step to breaking your habits! You've decided:
                    </Text>

                    <InfoCard
                        variant="primary"
                        childrenComponent={
                            <Text body2 classes="normal-case font-semibold pl-2">
                                “{actionStr.join(' ')}”
                            </Text>
                        }
                    />
                </div>
            </section>
            <section
                className={`px-6 py-8 flex flex-col gap-5  w-full mx-auto ${
                    isPopup ? 'md:w-3/4 md:px-0 md:py-10' : 'md:w-3/4 md:px-12 md:py-10 lg:w-1/2'
                } `}>
                <Heading h2 classes="font-bold">
                    Based on the technique you chose, here’s your action plan to help you achieve your goal:
                </Heading>
                <Text body>
                    Your action plan contains the steps our expert coaches think are most important when it comes to
                    making your own plan.
                </Text>

                <div className="flex flex-col gap-6 mt-3">
                    {actionList &&
                        actionList.map((action, index) => (
                            <StepCard
                                classes="flex items-start justify-start"
                                key={`${action.title}-${index}`}
                                stepText={index + 1}
                                title={action.title}
                                body={action.text}
                            />
                        ))}
                    <div className="flex flex-col gap-3 p-6 shadow-card rounded-xl">
                        <Heading h3 classes="font-medium">
                            Send yourself this action plan
                        </Heading>
                        <Text body>
                            Enter your email address and we&apos;ll send you over a copy of this action plan to refer
                            back to!
                        </Text>

                        {success ? (
                            <InfoCard
                                classes="flex items-center justify-center md:w-fit"
                                childrenComponent={
                                    <div className="flex flex-col gap-2 items-center justify-center p-4 w-full">
                                        <Text body2 classes="normal-case">
                                            Email was sent to:
                                        </Text>
                                        <Heading h3 classes="font-bold no-text-tranform text-26">
                                            {email}
                                        </Heading>
                                        <Button
                                            mode="underlineBtnSecondary"
                                            label="Change Email Address"
                                            classes="p-0 font-light text-16"
                                            onClick={() => setSuccess(false)}
                                        />
                                    </div>
                                }
                            />
                        ) : (
                            <Form
                                onSubmit={sendEmail}
                                validate={(values) => {
                                    const errors = {};
                                    if (!values.email) {
                                        errors.email = 'Required';
                                    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)) {
                                        errors.email = 'Please enter a valid email address';
                                    }
                                    return errors;
                                }}
                                initialValues={{
                                    email: '',
                                }}>
                                {({ handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <div className="h-auto w-full md:w-1/2">
                                            <Field
                                                name="email"
                                                input="email"
                                                label="Email Address"
                                                placeholder="e.g joebloggs@email.com"
                                                isRequired
                                            />
                                        </div>
                                        <Button
                                            isSubmit
                                            mode="primary"
                                            label="Send Action Plan"
                                            classes="mt-5 px-3 w-full justify-center md:w-auto"
                                        />
                                    </form>
                                )}
                            </Form>
                        )}
                    </div>
                </div>

                <InfoCard
                    childrenComponent={
                        <div className="flex flex-col gap-2 pl-2">
                            <Heading h3 classes="font-bold text-22">
                                Quick tip:
                            </Heading>
                            <Text body classes="normal-case">
                                {tip}
                            </Text>
                        </div>
                    }
                />
            </section>
            {articles && articles.length > 0 && (
                <section className="bg-primary-100 py-8">
                    <div
                        className={`px-6 flex flex-col justify-center gap-3 w-full mx-auto  ${
                            isPopup ? 'px-6 md:w-3/4' : 'md:w-3/4 lg:w-1/2'
                        } `}>
                        <Heading h2 classes="text-center">
                            Here&apos;s some articles that might help you to complete your action plan
                        </Heading>
                        <Carousel
                            data={
                                articles &&
                                articles.map((article) => (
                                    <Card
                                        ref={articleRef}
                                        key={article.title}
                                        classes="h-[495px] my-3"
                                        image={article.image}
                                        imageAlt={article.title}
                                        title={article.title}
                                        body={article.intro}
                                        btnLabel="Read More"
                                        btnOnClick={() =>
                                            window.open(`https://www.livewelldorset.co.uk/${article.url}`, '_blank')
                                        }
                                    />
                                ))
                            }
                        />
                    </div>
                </section>
            )}

            {apps && apps.length > 0 && (
                <section className="py-8">
                    <div
                        className={`px-6 flex flex-col gap-3 w-full mx-auto ${
                            isPopup ? 'px-6 md:w-3/4' : 'md:w-3/4 lg:w-1/2'
                        } `}>
                        <Heading h2 classes="text-center">
                            We also recommend these apps
                        </Heading>
                        <Carousel
                            data={apps.map((article) => (
                                <Card
                                    key={article.title}
                                    classes="h-[495px] my-3"
                                    image={article.image}
                                    imageAlt={article.title}
                                    title={article.title}
                                    body={article.intro}
                                    btnLabel="Find Out More"
                                    btnOnClick={() =>
                                        window.open(`https://www.livewelldorset.co.uk/${article.url}`, '_blank')
                                    }
                                />
                            ))}
                        />
                    </div>
                </section>
            )}

            <section className={`${showPurpleBanner ? 'bg-primary-100' : ''}`}>
                <div
                    className={`py-8 px-6 flex flex-col items-center mx-auto gap-6 md:flex-row-reverse md:gap-8 md:py-18 ${
                        isPopup ? 'max-w-lwd-758 md:px-0' : 'mx-auto max-w-lwd-758 lg:w-1/2 md:px-0'
                    }`}>
                    <div className="h-auto w-full lg:w-1/2 overflow-hidden rounded-md">
                        <Image src={HelpBlockImage} imgClass="rounded-md h-full object-left" video />
                    </div>
                    <div className="flex flex-col gap-3 w-full lg:w-1/2">
                        <Heading h2>Feel like you need more help?</Heading>
                        <Text body>
                            We understand it can be difficult to get started on your own. If you're feeling overwhelmed,
                            why not register for our free coaching, support and advice service - our friendly team of
                            real-life experts are here to help, funded by your local council.
                        </Text>

                        <div>
                            <a
                                href="https://my.livewelldorset.co.uk/register"
                                target="_blank"
                                rel="noreferrer noopener"
                                className="btn w-full mt-2 justify-center bg-primary-500 text-white md:w-fit">
                                Speak To An Expert
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </MainView>
    );
}

export default ActionPlan;
